import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLang } from '../context/LangContext';

// Contenu générique de départ pour les 3 pages légales attendues par un site e-commerce.
// ⚠️ À COMPLÉTER avec les informations réelles de l'entreprise avant l'ouverture au public :
// raison sociale exacte, matricule fiscal / RNE, adresse du siège, délais réels de retour,
// moyens de paiement effectivement proposés, etc. Le contenu ci-dessous est un point de
// départ raisonnable, pas un avis juridique.

const PLACEHOLDER = {
  company:  '[Raison sociale à compléter]',
  taxId:    '[Matricule fiscal / RNE à compléter]',
  address:  '[Adresse du siège à compléter], Tunisie',
  email:    'contact@matrix-electronic.tn',
  phone:    '[Téléphone à compléter]',
};

const PAGES = {
  cgv: {
    titleKey: 'legal_cgv_title',
    title_fr: 'Conditions Générales de Vente',
    title_en: 'Terms of Sale',
    body_fr: [
      ['1. Objet', `Les présentes conditions générales de vente régissent les ventes effectuées sur le site Matrix Electronic Devices, exploité par ${PLACEHOLDER.company} (${PLACEHOLDER.taxId}), ${PLACEHOLDER.address}.`],
      ['2. Produits et prix', "Les prix affichés sont exprimés en dinars tunisiens (TND), toutes taxes comprises. Matrix Electronic Devices se réserve le droit de modifier ses prix à tout moment, les produits étant facturés sur la base des tarifs en vigueur au moment de la validation de la commande."],
      ['3. Commande', "Toute commande passée sur le site vaut acceptation des présentes conditions. Une confirmation est envoyée après validation. La commande peut être annulée ou modifiée par le client tant qu'elle n'a pas été expédiée, en nous contactant directement."],
      ['4. Paiement', "Le paiement s'effectue au choix du client parmi les moyens proposés sur le site (paiement à la livraison, virement bancaire). [Détailler ici les modalités précises, notamment pour le virement : IBAN, délai de traitement, etc.]"],
      ['5. Livraison', "Les délais de livraison sont communiqués à titre indicatif lors de la commande. Matrix Electronic Devices ne saurait être tenu responsable des retards imputables au transporteur."],
      ['6. Garantie', "Les produits vendus bénéficient de la garantie légale de conformité et, le cas échéant, d'une garantie commerciale du fabricant dont la durée est précisée sur la fiche produit."],
      ['7. Litiges', `Pour toute réclamation, le client peut contacter le service client à ${PLACEHOLDER.email} ou au ${PLACEHOLDER.phone}. À défaut d'accord amiable, les tribunaux tunisiens compétents seront seuls saisis.`],
    ],
  },
  retours: {
    titleKey: 'legal_returns_title',
    title_fr: 'Politique de Retour',
    title_en: 'Return Policy',
    body_fr: [
      ['1. Délai de rétractation', "[À confirmer selon votre politique commerciale — exemple courant : le client dispose de 7 jours ouvrables à compter de la réception du produit pour demander un retour ou un échange.]"],
      ['2. Conditions de retour', "Le produit doit être retourné dans son emballage d'origine, non utilisé, avec l'ensemble des accessoires et la preuve d'achat. Les produits personnalisés, descellés (écouteurs, etc.) ou endommagés par le client ne sont pas repris."],
      ['3. Procédure', `Pour initier un retour, contactez-nous via la page Contact ou au ${PLACEHOLDER.email} en précisant votre numéro de commande et le motif du retour. Nous vous indiquerons la marche à suivre.`],
      ['4. Remboursement', "Après réception et vérification du produit retourné, le remboursement (ou l'échange) est effectué selon le même moyen de paiement que celui utilisé lors de l'achat, dans un délai de [X] jours ouvrables."],
      ['5. Produit défectueux', "Si le produit reçu est défectueux ou non conforme à la commande, contactez-nous immédiatement : les frais de retour sont alors pris en charge par Matrix Electronic Devices."],
    ],
  },
  confidentialite: {
    titleKey: 'legal_privacy_title',
    title_fr: 'Politique de Confidentialité',
    title_en: 'Privacy Policy',
    body_fr: [
      ['1. Données collectées', "Lors d'une commande ou d'un message via le formulaire de contact, nous collectons : nom, prénom, adresse email, numéro de téléphone, adresse de livraison. Ces informations sont nécessaires au traitement de votre commande ou de votre demande."],
      ['2. Utilisation des données', "Vos données sont utilisées exclusivement pour traiter vos commandes, vous contacter au sujet de votre commande, et répondre à vos demandes. Elles ne sont ni vendues ni cédées à des tiers à des fins commerciales."],
      ['3. Conservation', "Les données liées aux commandes sont conservées pour la durée nécessaire au traitement de la commande et au respect de nos obligations légales et comptables."],
      ['4. Vos droits', `Conformément à la législation tunisienne relative à la protection des données à caractère personnel, vous disposez d'un droit d'accès, de rectification et de suppression de vos données. Pour l'exercer, contactez-nous à ${PLACEHOLDER.email}.`],
      ['5. Cookies', "Le site peut utiliser des cookies techniques nécessaires à son bon fonctionnement (panier, préférences de langue). Aucun cookie publicitaire tiers n'est utilisé à ce jour."],
    ],
  },
};

const LegalPage = () => {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const page = PAGES[slug];

  if (!page) return (
    <div className="not-found-page">
      <span>📄</span>
      <h2>{t('not_found')}</h2>
      <Link to="/" className="btn btn--primary">{t('back_home')}</Link>
    </div>
  );

  const title = lang === 'fr' ? page.title_fr : page.title_en;

  return (
    <div className="legal-page">
      <div className="breadcrumb">
        <div className="breadcrumb__inner">
          <Link to="/">{t('nav_home')}</Link> <span>›</span>
          <span>{title}</span>
        </div>
      </div>

      <div className="legal-page__inner">
        <h1>{title}</h1>
        <p className="legal-page__disclaimer">
          ⚠️ Contenu de référence à valider/compléter avec vos informations légales réelles avant mise en ligne (raison sociale, matricule fiscal, délais exacts...).
        </p>
        {page.body_fr.map(([heading, text]) => (
          <section key={heading} className="legal-page__section">
            <h2>{heading}</h2>
            <p>{text}</p>
          </section>
        ))}
      </div>
    </div>
  );
};

export default LegalPage;
